/**
 * AeriosIQ brand assets — vector data verbatim from the designer file
 * `ARIOS2.1.svg` (delivered 2026-07-08, archived in the orchestration repo
 * under .vibe/_TRANSIST_FROM_WP_TO_AERIOSIQ/). No hand-tracing.
 *
 * The lockup is deliberately split into independently swappable parts
 * (transition doc 05 SS2): Mark (gradient network shape), Wordmark
 * ("Aerios" letterforms with dotless-i rect, currentColor), Badge
 * (gradient "IQ", Q as magnifier). Swap any part by editing its file only
 * — the composite AeriosIQLogo re-uses the exported path constants at the
 * designer's original coordinates, so spacing never drifts.
 *
 * Gradient (authoritative): #00829F at 0% -> #00303F at 77%.
 * Dark-surface treatment: deferred to the CI pass — the gradient is tuned
 * for light chrome; wordmark letters follow currentColor.
 */
import type { SVGProps } from "react";
import { useTheme } from "@mui/material/styles";

/**
 * Theme-aware fill (Christian, 2026-07-08): dark theme renders the ENTIRE
 * logo monochrome white (gradient's petrol end sinks into dark chrome);
 * light theme keeps the colored gradient. `variant` overrides the
 * auto-detection when a surface needs a fixed treatment.
 */
type LogoVariant = "auto" | "color" | "mono";

import { AERIOSIQ_MARK_PATH } from "./AeriosIQMark.js";
import { AERIOSIQ_BADGE_PATH } from "./AeriosIQBadge.js";
import {
  AERIOSIQ_WORDMARK_PATHS,
  AERIOSIQ_WORDMARK_RECT,
} from "./AeriosIQWordmark.js";

/** Full lockup at the designer's original coordinates — Mark + "Aerios" +
 * IQ badge. Letters follow currentColor; mark + badge keep the gradient. */
export function AeriosIQLogo({
  variant = "auto",
  ...props
}: SVGProps<SVGSVGElement> & { variant?: LogoVariant }) {
  const mode = useTheme().palette.mode;
  const mono = variant === "mono" || (variant === "auto" && mode === "dark");
  return (
    <svg
      // eslint-disable-next-line no-restricted-syntax -- SVG namespace URI is canonical http://; not an OWASP V9.1.1 concern.
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 312.36 49.96"
      role="img"
      aria-label="AeriosIQ"
      height={24}
      fill="currentColor"
      {...props}
    >
      <defs>
        <linearGradient
          id="aiq-grad-logo-m"
          x1="0"
          y1="25"
          x2="49.74"
          y2="25"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#00829f" />
          <stop offset=".77" stopColor="#00303f" />
        </linearGradient>
        <linearGradient
          id="aiq-grad-logo-b"
          x1="280.91"
          y1="12.82"
          x2="312.36"
          y2="12.82"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#00829f" />
          <stop offset=".77" stopColor="#00303f" />
        </linearGradient>
      </defs>
      <path
        fill={mono ? "currentColor" : "url(#aiq-grad-logo-m)"}
        d={AERIOSIQ_MARK_PATH}
      />
      {AERIOSIQ_WORDMARK_PATHS.map((d) => (
        <path key={d.slice(0, 24)} d={d} />
      ))}
      <rect
        x={AERIOSIQ_WORDMARK_RECT.x}
        y={AERIOSIQ_WORDMARK_RECT.y}
        width={AERIOSIQ_WORDMARK_RECT.width}
        height={AERIOSIQ_WORDMARK_RECT.height}
      />
      <path
        fill={mono ? "currentColor" : "url(#aiq-grad-logo-b)"}
        d={AERIOSIQ_BADGE_PATH}
      />
    </svg>
  );
}
